"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { DensityChart } from "@/components/tools/stats-distributions/charts";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import type {
  SampleNumbers,
  Z_TEST_CALCULATOR_STRINGS,
} from "@/components/tools/z-test-calculator";
import { t } from "@/lib/i18n/interpolate";
import { normalPdf } from "@/lib/stats/distributions";
import { densityGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { ZTestResult } from "@/lib/tool-kit/stats-tests-more/z-test";

/** Results for the z-test calculator. */

type Strings = typeof Z_TEST_CALCULATOR_STRINGS;

const FAR = 12;

/** The rejection region at α, as intervals on the z axis. */
function rejectionRegion(r: ZTestResult): [number, number][] {
  const c = r.criticalAlpha;
  if (r.alternative === "two-sided") {
    return [
      [-FAR, -c],
      [c, FAR],
    ];
  }
  return r.alternative === "less" ? [[-FAR, -c]] : [[c, FAR]];
}

export function ZTestResults({
  r,
  samples,
  alpha,
  strings,
  fmt,
}: {
  r: ZTestResult;
  samples: readonly SampleNumbers[];
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const one = r.kind === "one-sample";
  const reject = r.p < alpha;
  const levelText = fmt(r.level, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const bound = Math.max(4.5, Math.abs(r.z) + 0.5);
  const geometry = densityGeometry({
    pdf: (x) => normalPdf(x),
    xMin: -bound,
    xMax: bound,
    shaded: rejectionRegion(r),
    markers: [r.z],
  });
  const apa = richTemplate(
    one ? strings.apaOneTemplate : strings.apaTwoTemplate,
    {
      m: fixed(r.estimate, 2, fmt),
      z: fixed(r.z, 2, fmt),
      p: apaP(r.p, fmt),
      level: levelText,
      lo: fixed(r.ciLow, 2, fmt),
      hi: fixed(r.ciHigh, 2, fmt),
      d: fixed(r.d, 2, fmt),
    }
  );
  const [first, second] = samples;
  const criticalText = {
    "two-sided": `±${n(r.criticalAlpha)}`,
    less: `−${n(r.criticalAlpha)}`,
    greater: n(r.criticalAlpha),
  }[r.alternative];
  const sigmaForD = one
    ? (first.sigma ?? 0)
    : Math.sqrt(((first.sigma ?? 0) ** 2 + (second?.sigma ?? 0) ** 2) / 2);
  const raw = samples.filter((sample) => sample.sd !== null);
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.zStatistic} value={n(r.z)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard
          label={one ? strings.estimateOne : strings.estimateTwo}
          value={n(r.estimate)}
        />
        <StatCard label={strings.standardError} value={n(r.se)} />
        <StatCard
          label={t(strings.ciLabel, { level: levelText })}
          value={`${n(r.ciLow)} – ${n(r.ciHigh)}`}
        />
        <StatCard label={strings.cohenD} value={n(r.d)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
      <DensityChart
        format={(value) => fmt(value, { maximumFractionDigits: 1 })}
        geometry={geometry}
        label={strings.chartLabel}
      />
      <p className={HINT}>
        {t(strings.chartCaption, { alpha: fmt(alpha), z: n(r.z) })}{" "}
        {t(strings.criticalLine, { alpha: fmt(alpha), value: criticalText })}
      </p>
      <h3 className={WIDGET_H3}>{strings.pValuesHeading}</h3>
      <ResultTable
        head={[strings.alternative, strings.p]}
        rows={[
          {
            key: "two",
            cells: [
              one ? strings.twoSidedOne : strings.twoSidedTwo,
              formatP(r.pTwoSided, fmt),
            ],
          },
          {
            key: "less",
            cells: [
              one ? strings.lessOne : strings.lessTwo,
              formatP(r.pLess, fmt),
            ],
          },
          {
            key: "greater",
            cells: [
              one ? strings.greaterOne : strings.greaterTwo,
              formatP(r.pGreater, fmt),
            ],
          },
        ]}
      />
      {raw.length > 0 ? (
        <div className="space-y-1 text-foreground text-sm">
          <h3 className={WIDGET_H3}>{strings.sampleSummaryHeading}</h3>
          {samples.map((sample, index) =>
            sample.sd === null ? null : (
              <p key={`sample-${index + 1}`}>
                {t(strings.sampleSummary, {
                  n: fmt(index + 1),
                  count: fmt(sample.n),
                  mean: n(sample.mean),
                  sd: n(sample.sd),
                })}
              </p>
            )
          )}
        </div>
      ) : null}
      <Working heading={strings.howCalculated}>
        {one ? (
          <>
            <p>
              {t(strings.workingSeOne, {
                sigma: n(first.sigma ?? 0),
                n: fmt(first.n),
                se: n(r.se),
              })}
            </p>
            <p>
              {t(strings.workingZOne, {
                mean: n(r.estimate),
                mu0: n(r.nullValue),
                se: n(r.se),
                z: n(r.z),
              })}
            </p>
          </>
        ) : (
          <>
            <p>
              {t(strings.workingSeTwo, {
                s1: n(first.sigma ?? 0),
                n1: fmt(first.n),
                s2: n(second?.sigma ?? 0),
                n2: fmt(second?.n ?? 0),
                se: n(r.se),
              })}
            </p>
            <p>
              {t(strings.workingZTwo, {
                diff: n(r.estimate),
                delta0: n(r.nullValue),
                se: n(r.se),
                z: n(r.z),
              })}
            </p>
          </>
        )}
        <p>
          {t(strings.workingP, {
            p: formatP(r.pTwoSided, fmt),
            level: levelText,
            crit: n(r.zCritical),
          })}
        </p>
        <p>
          {t(strings.workingCi, {
            level: levelText,
            est: n(r.estimate),
            crit: n(r.zCritical),
            se: n(r.se),
            lo: n(r.ciLow),
            hi: n(r.ciHigh),
          })}
        </p>
        <p>
          {t(strings.workingD, {
            num: n(r.estimate - r.nullValue),
            den: n(sigmaForD),
            d: n(r.d),
          })}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
